import { useEffect, useMemo, useRef, useState } from 'react';
import { getEmbeddingMap } from '../lib/api';
import type { MapPoint } from '../lib/types';

// 2D projection of the knowledge base. Each dot is one chunk; clusters are
// topics the reducer found in the embedding space.
//
// When an answer arrives, the chunks it used light up. That highlight is the
// point of this view — it shows what the answer was built from rather than
// asking the reader to trust it. The question itself is deliberately NOT
// plotted: projecting a new vector into a fitted UMAP space puts a dot
// somewhere plausible-looking that does not actually mean anything.
const SIZE = 1000; // viewBox units; the SVG scales to its container
const PAD = 40;

// Colour carries the topic, not the source — every chunk is source='website',
// so colouring by source produced one colour and told the reader nothing.
// Distinct hues, readable side by side, no red (reserved for the highlight).
const PALETTE = [
  '#4c78a8', '#54a24b', '#b279a2', '#439894', '#9d755d',
  '#e2a336', '#7b8cbf', '#5aa8a0', '#a37c5b', '#6b8f3c',
];
const OTHER = '#b9b9b9';
const HIT = '#d94f4f';

interface Props {
  /** source_ref values from the latest answer's sources[] — these get highlighted. */
  highlightRefs?: string[];
}

export function KnowledgeMap({ highlightRefs = [] }: Props) {
  const [points, setPoints] = useState<MapPoint[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hover, setHover] = useState<MapPoint | null>(null);
  const [focus, setFocus] = useState<string | null>(null); // section being isolated
  const [view, setView] = useState({ x: 0, y: 0, k: 1 });
  const drag = useRef<{ x: number; y: number } | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  // The SVG is 1000 viewBox units wide but rendered at whatever CSS width the
  // container gives it. Screen pixels therefore have to be converted before
  // they can move anything, or panning drifts away from the cursor.
  const unitsPerPx = () => {
    const w = svgRef.current?.getBoundingClientRect().width;
    return w ? SIZE / w : 1;
  };

  useEffect(() => {
    let cancelled = false;
    getEmbeddingMap()
      .then((res) => !cancelled && setPoints(res.points))
      .catch((e) => !cancelled && setError(e.message));
    return () => { cancelled = true; };
  }, []);

  const highlighted = useMemo(() => new Set(highlightRefs), [highlightRefs]);

  // Biggest sections get their own colour; the long tail shares grey, so the
  // legend stays readable instead of listing forty one-page sections.
  const { colourOf, legend } = useMemo(() => {
    const counts = new Map<string, number>();
    for (const p of points ?? []) counts.set(p.section, (counts.get(p.section) ?? 0) + 1);
    const ranked = [...counts.entries()].sort((a, b) => b[1] - a[1]);
    const top = ranked.slice(0, PALETTE.length);
    const map = new Map(top.map(([s], i) => [s, PALETTE[i]]));
    return {
      colourOf: (s: string) => map.get(s) ?? OTHER,
      legend: top.map(([section, count]) => ({ section, count, colour: map.get(section)! })),
    };
  }, [points]);

  if (error) return <p className="map-empty">Kaart niet beschikbaar: {error}</p>;
  if (!points) return <p className="map-empty">Kenniskaart laden…</p>;
  if (!points.length) {
    return <p className="map-empty">Nog geen kaart. Draai <code>npm run embedding-map</code>.</p>;
  }

  const scale = (v: number) => PAD + v * (SIZE - PAD * 2);

  // Zoom about the pointer, not the origin. Scaling from (0,0) pushed the whole
  // cloud down-right off the canvas, which is what made zooming feel broken.
  function onWheel(e: React.WheelEvent) {
    e.preventDefault();
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return;
    const u = SIZE / rect.width;
    // pointer position in viewBox coordinates
    const px = (e.clientX - rect.left) * u;
    const py = (e.clientY - rect.top) * u;

    setView((v) => {
      const k = Math.min(12, Math.max(1, v.k * (e.deltaY < 0 ? 1.2 : 1 / 1.2)));
      if (k === v.k) return v;
      // keep the point under the cursor fixed while the scale changes
      const ratio = k / v.k;
      return { k, x: px - (px - v.x) * ratio, y: py - (py - v.y) * ratio };
    });
  }

  return (
    <div className="knowledge-map">
      <svg
        ref={svgRef}
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        role="img"
        aria-label="Kennisbron-kaart"
        onWheel={onWheel}
        onPointerDown={(e) => {
          if (view.k === 1) return; // nothing to pan at default zoom
          drag.current = { x: e.clientX, y: e.clientY };
        }}
        onPointerMove={(e) => {
          if (!drag.current) return;
          const u = unitsPerPx();
          const dx = (e.clientX - drag.current.x) * u;
          const dy = (e.clientY - drag.current.y) * u;
          drag.current = { x: e.clientX, y: e.clientY };
          setView((v) => ({ ...v, x: v.x + dx, y: v.y + dy }));
        }}
        onPointerUp={() => { drag.current = null; }}
        onPointerLeave={() => { drag.current = null; setHover(null); }}
      >
        <g transform={`translate(${view.x} ${view.y}) scale(${view.k})`}>
          {/* Highlighted points render last so they sit above the cloud. */}
          {[...points]
            .sort((a, b) => Number(highlighted.has(a.ref)) - Number(highlighted.has(b.ref)))
            .map((p) => {
              const isHit = highlighted.has(p.ref);
              const dimmed = focus !== null && p.section !== focus && !isHit;
              return (
                <circle
                  key={p.id}
                  cx={scale(p.x)}
                  cy={scale(p.y)}
                  r={(isHit ? 9 : 4) / view.k}
                  fill={isHit ? HIT : colourOf(p.section)}
                  fillOpacity={dimmed ? 0.08 : isHit ? 1 : 0.7}
                  onMouseEnter={() => setHover(p)}
                  onClick={() => p.ref.startsWith('http') && window.open(p.ref, '_blank', 'noopener')}
                  style={{ cursor: p.ref.startsWith('http') ? 'pointer' : 'default' }}
                >
                  <title>{p.title || p.ref}</title>
                </circle>
              );
            })}
        </g>
      </svg>

      <div className="map-legend">
        {legend.map((l) => (
          <button
            key={l.section}
            className={`map-chip${focus === l.section ? ' map-chip-on' : ''}`}
            onClick={() => setFocus(focus === l.section ? null : l.section)}
            title={`${l.count} fragmenten`}
          >
            <span className="map-swatch" style={{ background: l.colour }} />
            {l.section}
          </button>
        ))}
      </div>

      <div className="map-status">
        <span>{points.length} fragmenten</span>
        {highlighted.size > 0 && <span className="map-hit">{highlighted.size} gebruikt voor dit antwoord</span>}
        {view.k > 1 && (
          <button className="map-reset" onClick={() => setView({ x: 0, y: 0, k: 1 })}>herstel weergave</button>
        )}
        {hover && <span className="map-hover">{hover.title || hover.ref}</span>}
      </div>
    </div>
  );
}
